import { useEffect, useMemo, useState } from "react";
import { FileText, Layers, Plus, Upload, Eye, ChevronDown, ChevronUp, X, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  listOnboardingDocuments, createOnboardingDocument, updateOnboardingDocument, newDocumentVersion, documentFileUrl,
  listOnboardingPackets, createOnboardingPacket, updateOnboardingPacket, previewOnboardingPacket,
} from "@/lib/api";

const REQUIREMENTS = ["Required", "Optional"];
const ACTIONS = [
  { v: "information", l: "Information (Review)" },
  { v: "acknowledgment", l: "Acknowledgment (Acknowledge)" },
  { v: "signature", l: "Signature (Sign)" },
];
const ACTION_LABEL = { information: "Review", acknowledgment: "Acknowledge", signature: "Sign" };

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid={testid} style={{ maxWidth: 560 }}>
        <div className="modal-head" style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ flex: 1 }}>{title}</h2>
          <button className="btn sm ghost" onClick={onClose} data-testid="modal-close" aria-label="Close"><X size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

/* ------------------------- Document Library ------------------------- */
function DocumentForm({ initial, requireFile, showVersion, onSubmit, submitLabel }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState(initial?.title || "");
  const [employer, setEmployer] = useState(initial?.employer || "Hope's Cookies");
  const [effectiveDate, setEffectiveDate] = useState(initial?.effectiveDate || "");
  const [version, setVersion] = useState(initial?.version || "1.0");
  const [requirement, setRequirement] = useState(initial?.requirement || "Required");
  const [action, setAction] = useState(initial?.action || "acknowledgment");
  const [active, setActive] = useState(initial?.active ?? true);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (requireFile && !file) return toast.error("Please choose a document file to upload.");
    if (!title.trim()) return toast.error("A title is required.");
    setBusy(true);
    try {
      await onSubmit({ file, title: title.trim(), employer: employer.trim(), effectiveDate, version, requirement, action, active });
    } finally { setBusy(false); }
  };

  return (
    <div className="oa-form">
      {requireFile && (
        <label className="oa-file" data-testid="doc-file-input-wrap">
          <Upload size={16} /> {file ? file.name : "Choose document file (PDF, DOCX, image)…"}
          <input type="file" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="doc-file-input" style={{ display: "none" }} />
        </label>
      )}
      <label className="oa-lbl">Title
        <input className="ca-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title" placeholder="e.g. Code of Conduct" />
      </label>
      <div className="oa-grid2">
        <label className="oa-lbl">Employer / worksite
          <input className="ca-input" value={employer} onChange={(e) => setEmployer(e.target.value)} data-testid="doc-employer" />
        </label>
        <label className="oa-lbl">Effective date
          <input className="ca-input" type="date" value={effectiveDate || ""} onChange={(e) => setEffectiveDate(e.target.value)} data-testid="doc-effective" />
        </label>
      </div>
      <div className="oa-grid2">
        <label className="oa-lbl">Requirement
          <select className="ca-input" value={requirement} onChange={(e) => setRequirement(e.target.value)} data-testid="doc-requirement">
            {REQUIREMENTS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="oa-lbl">Action type
          <select className="ca-input" value={action} onChange={(e) => setAction(e.target.value)} data-testid="doc-action">
            {ACTIONS.map((a) => <option key={a.v} value={a.v}>{a.l}</option>)}
          </select>
        </label>
      </div>
      {showVersion && (
        <label className="oa-lbl">Version
          <input className="ca-input" value={version} onChange={(e) => setVersion(e.target.value)} data-testid="doc-version" placeholder="e.g. 1.0" />
        </label>
      )}
      {initial && !showVersion && (
        <label className="ca-ack" data-testid="doc-active-wrap">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="doc-active" />
          <span>Active (available to include in packets & assign to new candidates)</span>
        </label>
      )}
      <button className="mh-save" disabled={busy} onClick={submit} data-testid="doc-submit">{busy ? "Saving…" : submitLabel}</button>
    </div>
  );
}

function DocumentLibrary({ docs, reload }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [versioning, setVersioning] = useState(null);
  const [openHistory, setOpenHistory] = useState({});

  // Group by familyId: newest (active) first for the current row.
  const families = useMemo(() => {
    const map = new Map();
    for (const d of docs) {
      const k = d.familyId || d.id;
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(d);
    }
    const out = [];
    for (const [k, list] of map.entries()) {
      list.sort((a, b) => (b.createdAt || "").localeCompare(a.createdAt || ""));
      const current = list.find((d) => d.active) || list[0];
      const history = list.filter((d) => d.id !== current.id);
      out.push({ familyId: k, current, history });
    }
    return out;
  }, [docs]);

  const doCreate = async (payload) => {
    const fd = new FormData();
    fd.append("file", payload.file);
    fd.append("title", payload.title);
    fd.append("employer", payload.employer);
    if (payload.effectiveDate) fd.append("effectiveDate", payload.effectiveDate);
    fd.append("version", payload.version || "1.0");
    fd.append("requirement", payload.requirement);
    fd.append("action", payload.action);
    fd.append("active", String(payload.active));
    await createOnboardingDocument(fd);
    toast.success("Document uploaded to the library.");
    setShowAdd(false); reload();
  };

  const doEdit = async (payload) => {
    await updateOnboardingDocument(editing.id, {
      title: payload.title, employer: payload.employer, effectiveDate: payload.effectiveDate,
      requirement: payload.requirement, action: payload.action, active: payload.active,
    });
    toast.success("Document details updated. Existing candidate records are unchanged.");
    setEditing(null); reload();
  };

  const doVersion = async (payload) => {
    const fd = new FormData();
    fd.append("file", payload.file);
    fd.append("version", payload.version);
    fd.append("title", payload.title);
    if (payload.effectiveDate) fd.append("effectiveDate", payload.effectiveDate);
    fd.append("requirement", payload.requirement);
    fd.append("action", payload.action);
    await newDocumentVersion(versioning.id, fd);
    toast.success("New version published. New candidates receive it; existing records stay frozen.");
    setVersioning(null); reload();
  };

  const toggleActive = async (d) => {
    await updateOnboardingDocument(d.id, { active: !d.active });
    toast.success(d.active ? "Document deactivated." : "Document activated.");
    reload();
  };

  return (
    <div data-testid="document-library">
      <div className="oa-secbar">
        <p className="mut" style={{ margin: 0, flex: 1 }}>MIHP-controlled policy documents. Uploading a New Version freezes previous candidate snapshots — they are never altered.</p>
        <button className="btn primary" onClick={() => setShowAdd(true)} data-testid="add-document-btn"><Plus size={15} style={{ verticalAlign: "-2px" }} /> Upload document</button>
      </div>

      <div className="oa-list">
        {families.map(({ familyId, current, history }) => (
          <div key={familyId} className="oa-card" data-testid={`doc-family-${familyId}`}>
            <div className="oa-row">
              <div className="oa-main">
                <div className="oa-title">{current.title}
                  <span className={`oa-badge ${current.active ? "on" : "off"}`} data-testid={`doc-active-badge-${current.id}`}>{current.active ? "Active" : "Inactive"}</span>
                  <span className="oa-ver" data-testid={`doc-version-${current.id}`}>v{current.version}</span>
                </div>
                <div className="mut oa-meta">
                  {current.employer} · {current.requirement} · {ACTION_LABEL[current.action]}
                  {current.effectiveDate ? ` · effective ${current.effectiveDate}` : ""}
                  {!current.file && <span className="oa-nofile"> · no file (legacy)</span>}
                </div>
              </div>
              <div className="oa-actions">
                {current.file
                  ? <a className="btn sm" href={documentFileUrl(current.id)} target="_blank" rel="noreferrer" data-testid={`doc-view-${current.id}`}><ExternalLink size={13} style={{ verticalAlign: "-2px" }} /> View</a>
                  : <span className="btn sm ghost" style={{ opacity: .5, pointerEvents: "none" }}>No file</span>}
                <button className="btn sm" onClick={() => setEditing(current)} data-testid={`doc-edit-${current.id}`}>Edit</button>
                <button className="btn sm" onClick={() => setVersioning(current)} data-testid={`doc-newversion-${current.id}`}>New version</button>
                <button className="btn sm ghost" onClick={() => toggleActive(current)} data-testid={`doc-toggle-${current.id}`}>{current.active ? "Deactivate" : "Activate"}</button>
              </div>
            </div>
            {history.length > 0 && (
              <div className="oa-hist">
                <button className="oa-histtoggle" onClick={() => setOpenHistory((s) => ({ ...s, [familyId]: !s[familyId] }))} data-testid={`doc-history-toggle-${familyId}`}>
                  {openHistory[familyId] ? <ChevronUp size={14} /> : <ChevronDown size={14} />} Version history ({history.length})
                </button>
                {openHistory[familyId] && (
                  <div data-testid={`doc-history-${familyId}`}>
                    {history.map((h) => (
                      <div key={h.id} className="oa-histrow">
                        <span>v{h.version} · {h.requirement} · {ACTION_LABEL[h.action]}{h.effectiveDate ? ` · ${h.effectiveDate}` : ""}</span>
                        <span className="oa-badge off">Superseded</span>
                        {h.file && <a className="btn sm ghost" href={documentFileUrl(h.id)} target="_blank" rel="noreferrer" data-testid={`doc-view-hist-${h.id}`}>View</a>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showAdd && (
        <Modal title="Upload onboarding document" onClose={() => setShowAdd(false)} testid="add-document-modal">
          <DocumentForm requireFile showVersion onSubmit={doCreate} submitLabel="Upload document" />
        </Modal>
      )}
      {editing && (
        <Modal title={`Edit — ${editing.title}`} onClose={() => setEditing(null)} testid="edit-document-modal">
          <p className="mut" style={{ marginTop: 0 }}>Editing details does not change the file or version, and never alters candidate records already issued. To replace the file, use <strong>New version</strong>.</p>
          <DocumentForm initial={editing} onSubmit={doEdit} submitLabel="Save changes" />
        </Modal>
      )}
      {versioning && (
        <Modal title={`New version — ${versioning.title}`} onClose={() => setVersioning(null)} testid="new-version-modal">
          <div className="mh-note" data-testid="version-warning" style={{ marginBottom: 12 }}>
            Existing candidate onboarding records will remain unchanged. New candidates will receive the new active version.
          </div>
          <DocumentForm initial={{ ...versioning, version: "" }} requireFile showVersion onSubmit={doVersion} submitLabel="Publish new version" />
        </Modal>
      )}
    </div>
  );
}

/* ------------------------- Packet Templates ------------------------- */
function PacketPreview({ packetId, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => { previewOnboardingPacket(packetId).then(setD).catch(() => setD({ items: [], packetName: "—" })); }, [packetId]);
  return (
    <Modal title="Packet preview" onClose={onClose} testid="packet-preview-modal">
      <div className="mh-note" data-testid="preview-disclaimer" style={{ marginBottom: 12 }}>
        Preview only — this mirrors exactly what a candidate would receive. Nothing is issued: no link, no candidate packet, no email, no history.
      </div>
      {!d ? <div className="mut">Loading…</div> : (
        <div className="oa-preview">
          <div className="ca-confirm-card" style={{ marginBottom: 12 }}>
            <div className="ca-emp">{d.packetName}</div>
            <div className="ca-loc">{d.employer}</div>
          </div>
          {d.items.map((it) => (
            <div key={it.documentId} className="permalink-url" data-testid={`preview-item-${it.order}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{it.order}. {it.title} {it.requirement === "Optional" && <span className="mut">(optional)</span>}</div>
                <div className="mut" style={{ fontSize: ".75rem" }}>v{it.version} · {ACTION_LABEL[it.action]} · {it.employer}</div>
              </div>
              <span className="btn sm" style={{ pointerEvents: "none" }}>{ACTION_LABEL[it.action]}</span>
            </div>
          ))}
          {d.items.length === 0 && <div className="mut">This packet has no documents yet.</div>}
        </div>
      )}
    </Modal>
  );
}

function PacketEditor({ packet, docs, onClose, onSaved }) {
  const activeDocs = docs.filter((d) => d.active);
  const [name, setName] = useState(packet?.name || "");
  const [employer, setEmployer] = useState(packet?.employer || "Hope's Cookies");
  const [ids, setIds] = useState(packet?.documentIds || []);
  const [busy, setBusy] = useState(false);
  const byId = useMemo(() => Object.fromEntries(docs.map((d) => [d.id, d])), [docs]);

  const add = (id) => setIds((s) => (s.includes(id) ? s : [...s, id]));
  const remove = (id) => setIds((s) => s.filter((x) => x !== id));
  const move = (i, dir) => setIds((s) => {
    const j = i + dir; if (j < 0 || j >= s.length) return s;
    const n = [...s]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const available = activeDocs.filter((d) => !ids.includes(d.id));

  const save = async () => {
    if (!name.trim()) return toast.error("A packet name is required.");
    setBusy(true);
    try {
      if (packet) { await updateOnboardingPacket(packet.id, { name: name.trim(), documentIds: ids }); toast.success("Packet template updated. Historical candidate packets are unchanged."); }
      else { await createOnboardingPacket({ name: name.trim(), employer: employer.trim(), documentIds: ids }); toast.success("Packet template created."); }
      onSaved();
    } finally { setBusy(false); }
  };

  return (
    <Modal title={packet ? `Edit packet — ${packet.name}` : "New packet template"} onClose={onClose} testid="packet-editor-modal">
      <label className="oa-lbl">Packet name
        <input className="ca-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="packet-name" placeholder="e.g. Hope's Cookies — New Hire Packet" />
      </label>
      {!packet && (
        <label className="oa-lbl">Employer / worksite
          <input className="ca-input" value={employer} onChange={(e) => setEmployer(e.target.value)} data-testid="packet-employer" />
        </label>
      )}
      <div className="oa-lbl">Documents in this packet (candidate order)</div>
      <div className="oa-seq" data-testid="packet-sequence">
        {ids.length === 0 && <div className="mut" style={{ padding: 8 }}>No documents added yet.</div>}
        {ids.map((id, i) => (
          <div key={id} className="oa-seqrow" data-testid={`packet-seq-${id}`}>
            <span className="oa-seqnum">{i + 1}</span>
            <span style={{ flex: 1 }}>{byId[id]?.title || "(unknown)"} <span className="mut">v{byId[id]?.version}</span></span>
            <button className="btn sm ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`packet-up-${id}`}><ChevronUp size={13} /></button>
            <button className="btn sm ghost" onClick={() => move(i, 1)} disabled={i === ids.length - 1} data-testid={`packet-down-${id}`}><ChevronDown size={13} /></button>
            <button className="btn sm ghost" onClick={() => remove(id)} data-testid={`packet-remove-${id}`}><X size={13} /></button>
          </div>
        ))}
      </div>
      {available.length > 0 && (
        <>
          <div className="oa-lbl" style={{ marginTop: 10 }}>Add a document</div>
          <div className="oa-addlist" data-testid="packet-add-list">
            {available.map((d) => (
              <button key={d.id} className="btn sm" onClick={() => add(d.id)} data-testid={`packet-add-${d.id}`}><Plus size={12} style={{ verticalAlign: "-1px" }} /> {d.title} <span className="mut">v{d.version}</span></button>
            ))}
          </div>
        </>
      )}
      <button className="mh-save" disabled={busy} onClick={save} data-testid="packet-save" style={{ marginTop: 14 }}>{busy ? "Saving…" : packet ? "Save packet" : "Create packet"}</button>
    </Modal>
  );
}

function PacketTemplates({ packets, docs, reload }) {
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new
  const [previewId, setPreviewId] = useState(null);
  return (
    <div data-testid="packet-templates">
      <div className="oa-secbar">
        <p className="mut" style={{ margin: 0, flex: 1 }}>Templates assigned by employer at onboarding start. Editing a template never alters packets already issued to candidates.</p>
        <button className="btn primary" onClick={() => setEditing(null)} data-testid="add-packet-btn"><Plus size={15} style={{ verticalAlign: "-2px" }} /> New packet</button>
      </div>
      <div className="oa-list">
        {packets.map((p) => (
          <div key={p.id} className="oa-card" data-testid={`packet-${p.id}`}>
            <div className="oa-row">
              <div className="oa-main">
                <div className="oa-title">{p.name}</div>
                <div className="mut oa-meta">{p.employer} · {(p.documentIds || []).length} document{(p.documentIds || []).length === 1 ? "" : "s"}</div>
              </div>
              <div className="oa-actions">
                <button className="btn sm" onClick={() => setPreviewId(p.id)} data-testid={`packet-preview-${p.id}`}><Eye size={13} style={{ verticalAlign: "-2px" }} /> Preview</button>
                <button className="btn sm" onClick={() => setEditing(p)} data-testid={`packet-edit-${p.id}`}>Edit</button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {editing !== undefined && (
        <PacketEditor packet={editing} docs={docs} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); reload(); }} />
      )}
      {previewId && <PacketPreview packetId={previewId} onClose={() => setPreviewId(null)} />}
    </div>
  );
}

/* ------------------------- Page ------------------------- */
export default function OnboardingAdmin() {
  const [tab, setTab] = useState("documents");
  const [docs, setDocs] = useState(null);
  const [packets, setPackets] = useState([]);

  const reload = () => {
    listOnboardingDocuments().then(setDocs).catch(() => setDocs([]));
    listOnboardingPackets().then(setPackets).catch(() => setPackets([]));
  };
  useEffect(() => { reload(); }, []);

  return (
    <div data-testid="onboarding-admin">
      <div className="record-head" style={{ marginBottom: 8 }}>
        <div>
          <h1 className="record-title">Onboarding Admin</h1>
          <div className="mut record-sub">Authorized MIHP workspace — document library & packet templates. MIHP-controlled onboarding only (no tax, banking, or I-9).</div>
        </div>
      </div>

      <div className="oa-tabs" role="tablist">
        <button className={`oa-tab${tab === "documents" ? " on" : ""}`} onClick={() => setTab("documents")} data-testid="tab-documents" role="tab"><FileText size={15} style={{ verticalAlign: "-2px" }} /> Document Library</button>
        <button className={`oa-tab${tab === "packets" ? " on" : ""}`} onClick={() => setTab("packets")} data-testid="tab-packets" role="tab"><Layers size={15} style={{ verticalAlign: "-2px" }} /> Packet Templates</button>
      </div>

      {docs === null ? <div className="empty" data-testid="oa-loading">Loading onboarding library…</div> : (
        tab === "documents"
          ? <DocumentLibrary docs={docs} reload={reload} />
          : <PacketTemplates packets={packets} docs={docs} reload={reload} />
      )}
    </div>
  );
}
